import { ReactNode, useState } from "react";
import {
	DndContext,
	DragEndEvent,
	DragOverlay,
	DragStartEvent,
	PointerSensor,
	pointerWithin,
	useSensor,
	useSensors,
} from "@dnd-kit/core";

import { MovablePriorityItem, movePriorityItem } from "./movePriorityItem";

export interface PriorityDefinitionBoardProps<
	TLevel extends string,
	TItem extends MovablePriorityItem<TLevel>,
> {
	/** The flat priority array both surfaces keep their definitions in. */
	items: TItem[];
	/** Called with the whole reordered array when a card is dropped in another box. */
	onItemsChange: (items: TItem[]) => void;
	/** The four `PriorityDefinitionSection`s, each rendered with `movable`. */
	children: ReactNode;
	className?: string;
}

/**
 * The four priority boxes as one drag-and-drop surface (PREC-3908).
 *
 * Re-prioritising a definition used to mean retyping it in the target box and deleting the
 * original. Wrapping the boxes in a single `DndContext` lets a card be dragged straight across;
 * the sections register as drop targets and the cards as draggables.
 *
 * `pointerWithin` rather than the default `closestCenter`: the cards are tall multi-line blocks
 * and the boxes are uneven in height, so "whichever box the pointer is actually inside" is the
 * only collision rule that matches where the user thinks they are dropping.
 *
 * Used by the general signal settings and the tracker list settings, which render the same
 * definitions.
 */
export function PriorityDefinitionBoard<
	TLevel extends string,
	TItem extends MovablePriorityItem<TLevel>,
>({ items, onItemsChange, children, className }: PriorityDefinitionBoardProps<TLevel, TItem>) {
	const [activeItem, setActiveItem] = useState<TItem | null>(null);

	// A small distance constraint so a click on the grip still reaches the button; without it
	// every pointer-down starts a drag and the handle stops behaving like a control.
	const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 4 } }));

	const handleDragStart = (event: DragStartEvent) => {
		setActiveItem(
			items.find((item) => `${item.priorityLevel}::${item.title}` === String(event.active.id)) ??
				null
		);
	};

	const handleDragEnd = (event: DragEndEvent) => {
		setActiveItem(null);

		if (!event.over) {
			return;
		}

		const next = movePriorityItem(items, String(event.active.id), event.over.id as TLevel);

		// movePriorityItem returns the same reference for a no-op drop, so a drag that ends where
		// it started never marks the settings form dirty.
		if (next !== items) {
			onItemsChange(next);
		}
	};

	return (
		<DndContext
			collisionDetection={pointerWithin}
			sensors={sensors}
			onDragCancel={() => setActiveItem(null)}
			onDragEnd={handleDragEnd}
			onDragStart={handleDragStart}
		>
			<div className={className}>{children}</div>
			<DragOverlay>
				{activeItem ? (
					<div className="p-3 rounded-lg border border-brand-light bg-white shadow-all">
						<h5 className="text-sm font-semibold text-text">{activeItem.title}</h5>
					</div>
				) : null}
			</DragOverlay>
		</DndContext>
	);
}
